import { FBXReader, FBXReaderNode } from 'fbx-parser';
import { Quat, Vec3 } from 'playcanvas';

import { Events } from './events';
import { FBX_TICKS_PER_SECOND, readAxisConverter, readNumericArray } from './fbx-parse-utils';

type Channel = {
    times: number[];   // seconds
    values: number[];
};

// linear-interpolate a sampled curve at time t (seconds), clamped at the ends
const sampleChannel = (channel: Channel | undefined, t: number, fallback: number): number => {
    if (!channel || channel.times.length === 0) {
        return fallback;
    }

    const { times, values } = channel;

    if (t <= times[0]) return values[0];
    if (t >= times[times.length - 1]) return values[values.length - 1];

    for (let i = 0; i < times.length - 1; i++) {
        if (t >= times[i] && t <= times[i + 1]) {
            const span = times[i + 1] - times[i];
            const frac = span > 0 ? (t - times[i]) / span : 0;
            return values[i] + (values[i + 1] - values[i]) * frac;
        }
    }

    return values[values.length - 1];
};

// read a single AnimationCurve node's KeyTime/KeyValueFloat arrays into a Channel
const readCurve = (curveNode: FBXReaderNode): Channel | undefined => {
    const keyTimes = readNumericArray(curveNode.node('KeyTime'));
    const keyValues = readNumericArray(curveNode.node('KeyValueFloat'));

    if (!keyTimes || !keyValues || keyTimes.length === 0) {
        return undefined;
    }

    return {
        times: keyTimes.map(t => t / FBX_TICKS_PER_SECOND),
        values: keyValues
    };
};

// resolve the X/Y/Z animation curves feeding a given AnimationCurveNode (e.g. "Lcl Translation")
// via the Connections table: C, "OP", curveId, curveNodeId, "d|X"/"d|Y"/"d|Z"
const readCurveNodeChannels = (
    reader: FBXReader,
    curveNodeId: number,
    curvesById: Map<number, FBXReaderNode>,
    connections: FBXReaderNode[]
): { x?: Channel, y?: Channel, z?: Channel } => {
    const result: { x?: Channel, y?: Channel, z?: Channel } = {};

    for (const c of connections) {
        const childId = c.prop(1, 'bigint') ?? c.prop(1, 'number');
        const parentId = c.prop(2, 'bigint') ?? c.prop(2, 'number');
        const axis = c.prop(3, 'string');

        if (parentId === undefined || Number(parentId) !== curveNodeId) continue;

        const curveNode = curvesById.get(Number(childId));
        if (!curveNode) continue;

        const channel = readCurve(curveNode);
        if (!channel) continue;

        if (axis === 'd|X') result.x = channel;
        else if (axis === 'd|Y') result.y = channel;
        else if (axis === 'd|Z') result.z = channel;
    }

    return result;
};

// find the AnimationCurveNode id feeding a given property (e.g. "Lcl Translation") of a model
const findCurveNodeId = (
    modelId: number,
    property: string,
    connections: FBXReaderNode[]
): number | undefined => {
    for (const c of connections) {
        const childId = c.prop(1, 'bigint') ?? c.prop(1, 'number');
        const parentId = c.prop(2, 'bigint') ?? c.prop(2, 'number');
        const prop = c.prop(3, 'string');

        if (parentId !== undefined && Number(parentId) === modelId && prop === property) {
            return Number(childId);
        }
    }
    return undefined;
};

const loadFbxCameraAnimation = (reader: FBXReader, events: Events, filename: string) => {
    const convertAxis = readAxisConverter(reader);

    // find a camera Model node
    const objects = reader.node('Objects');
    const models = objects?.nodes('Model') ?? [];
    const cameraModel = models.find(m => m.prop(2, 'string') === 'Camera');

    if (!cameraModel) {
        throw new Error('No camera found in FBX file');
    }

    const cameraId = Number(cameraModel.prop(0, 'bigint') ?? cameraModel.prop(0, 'number'));

    // index all AnimationCurveNode and AnimationCurve nodes by id
    const curveNodes = objects?.nodes('AnimationCurveNode') ?? [];
    const curves = objects?.nodes('AnimationCurve') ?? [];
    const curvesById = new Map<number, FBXReaderNode>();
    curves.forEach((c) => {
        const id = Number(c.prop(0, 'bigint') ?? c.prop(0, 'number'));
        curvesById.set(id, c);
    });
    const curveNodesById = new Map<number, FBXReaderNode>();
    curveNodes.forEach((c) => {
        const id = Number(c.prop(0, 'bigint') ?? c.prop(0, 'number'));
        curveNodesById.set(id, c);
    });

    const connections = reader.node('Connections')?.nodes('C') ?? [];

    const translationCurveNodeId = findCurveNodeId(cameraId, 'Lcl Translation', connections);
    const rotationCurveNodeId = findCurveNodeId(cameraId, 'Lcl Rotation', connections);

    const translation = translationCurveNodeId !== undefined ?
        readCurveNodeChannels(reader, translationCurveNodeId, curvesById, connections) : {};
    const rotation = rotationCurveNodeId !== undefined ?
        readCurveNodeChannels(reader, rotationCurveNodeId, curvesById, connections) : {};

    if (!translation.x && !translation.y && !translation.z && !rotation.x && !rotation.y && !rotation.z) {
        throw new Error('Camera in FBX file has no animation curves');
    }

    // collect every authored key time across all channels
    const allTimes = new Set<number>();
    [translation.x, translation.y, translation.z, rotation.x, rotation.y, rotation.z].forEach((ch) => {
        ch?.times.forEach(t => allTimes.add(t));
    });
    const sortedTimes = Array.from(allTimes).sort((a, b) => a - b);

    if (sortedTimes.length === 0) {
        throw new Error('Camera animation in FBX file has no keyframes');
    }

    const frameRate = events.invoke('timeline.frameRate') as number;
    const currentFrames = events.invoke('timeline.frames') as number;
    const duration = sortedTimes[sortedTimes.length - 1];
    const neededFrames = Math.ceil(duration * frameRate) + 1;
    if (neededFrames > currentFrames) {
        events.fire('timeline.setFrames', neededFrames);
    }

    const q = new Quat();
    const forward = new Vec3();

    sortedTimes.forEach((t, i) => {
        const tx = sampleChannel(translation.x, t, 0);
        const ty = sampleChannel(translation.y, t, 0);
        const tz = sampleChannel(translation.z, t, 0);
        const rx = sampleChannel(rotation.x, t, 0);
        const ry = sampleChannel(rotation.y, t, 0);
        const rz = sampleChannel(rotation.z, t, 0);

        const position = convertAxis(new Vec3(tx, ty, tz));

        q.setFromEulerAngles(rx, ry, rz);
        // rotate the FBX rotation's forward axis, then convert into app-space to match position
        forward.set(0, 0, -1);
        q.transformVector(forward, forward);
        const forwardConverted = convertAxis(new Vec3(position.x + forward.x, position.y + forward.y, position.z + forward.z))
        .sub(position);

        const target = position.clone().add(forwardConverted.mulScalar(10));

        events.fire('camera.addPose', {
            name: `${filename}_${i}`,
            frame: Math.round(t * frameRate),
            position,
            target
        });
    });
};

export { loadFbxCameraAnimation };
